<script setup>
import { computed, onBeforeUnmount } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { applyDatasetDimming, removeTooltipEl } from './barInteractions';

const props = defineProps({
  collection: {
    type: Object,
    default: () => ({}),
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
});

ChartJS.register(Title, Tooltip, BarElement, CategoryScale, LinearScale);

const fontFamily =
  'Excon,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif';

const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false,
    labels: {
      fontFamily,
    },
  },
  animation: {
    duration: 0,
  },
  datasets: {
    bar: {
      barPercentage: 1.0,
    },
  },
  scales: {
    x: {
      ticks: {
        fontFamily: fontFamily,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    y: {
      type: 'linear',
      position: 'left',
      ticks: {
        fontFamily: fontFamily,
        beginAtZero: true,
        stepSize: 1,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
};

const tooltipId = `cw-bar-tooltip-${Math.random().toString(36).slice(2)}`;

const ensureTooltipEl = id => {
  const existing = document.getElementById(id);
  if (existing) return existing;
  const el = document.createElement('div');
  el.id = id;
  el.style.cssText =
    'opacity:0;position:fixed;pointer-events:none;transform:translate(-50%,-100%);transition:opacity 120ms ease;z-index:9999;';
  document.body.appendChild(el);
  return el;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMaybeArrayLabel = label => {
  if (Array.isArray(label)) return String(label[0] ?? '');
  return String(label ?? '');
};

const defaultExternalTooltip = context => {
  const { chart, tooltip } = context;
  const el = ensureTooltipEl(tooltipId);

  if (!tooltip || tooltip.opacity === 0) {
    el.style.opacity = '0';
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  const dataIndex = dataPoint?.dataIndex;
  const datasetIndex = dataPoint?.datasetIndex;
  if (dataIndex == null || datasetIndex == null) return;

  const title = tooltip?.title?.length
    ? tooltip.title[0]
    : formatMaybeArrayLabel(chart?.data?.labels?.[dataIndex]);

  const rawValue = toNumber(dataPoint?.raw);
  const datasets = chart?.data?.datasets || [];
  const totalAtIndex = datasets.reduce((sum, ds) => {
    const v = Array.isArray(ds?.data) ? ds.data[dataIndex] : 0;
    return sum + toNumber(v);
  }, 0);

  const showPercent = datasets.length > 1 && totalAtIndex > 0;
  const percent = showPercent
    ? ((rawValue / totalAtIndex) * 100).toFixed(0)
    : null;
  const seriesLabel = String(dataPoint?.dataset?.label || '');
  const dotColor = tooltip?.labelColors?.[0]?.backgroundColor || '#cbd5e1';

  el.innerHTML = `
    <div style="min-width:220px;border:1px solid #d1d5db;background:#fff;border-radius:4px;box-shadow:rgba(0,0,0,0.2) 0 5px 10px 0;overflow:hidden;">
      <div style="background:#eff3f5;font-size:13px;line-height:20px;color:#111827;padding:4px 10px;text-align:center;">${String(title || '')}</div>
      <div style="font-size:13px;line-height:20px;color:#424d57;padding:10px 12px;background:#fff;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="width:10px;height:10px;border-radius:3px;background:${dotColor};display:inline-block;"></span>
          <span>
            <strong style="font-weight:600;color:#111827;">${rawValue.toLocaleString()}</strong>
            <span style="margin-left:4px;">${seriesLabel}</span>
            ${percent != null ? `<span style="color:#6b7280;margin-left:6px;">(${percent}%)</span>` : ''}
          </span>
        </div>
      </div>
    </div>
  `;

  const rect = chart.canvas.getBoundingClientRect();
  const tooltipWidth = el.offsetWidth || 220;
  const tooltipHeight = el.offsetHeight || 80;
  const left = clamp(
    rect.left + tooltip.caretX,
    tooltipWidth / 2 + 8,
    window.innerWidth - tooltipWidth / 2 - 8
  );
  const top = Math.max(rect.top + tooltip.caretY - 10, tooltipHeight + 8);

  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  el.style.opacity = '1';
};

const defaultOnHover = (_event, active, chart) => {
  const hoveredDatasetIndex = active?.length ? active[0].datasetIndex : null;
  applyDatasetDimming(chart, hoveredDatasetIndex, 0.25);
};

const isPlainObject = value =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const deepMerge = (target, source) => {
  if (!isPlainObject(source)) return target;
  Object.keys(source).forEach(key => {
    const srcVal = source[key];
    const tgtVal = target[key];
    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      target[key] = deepMerge({ ...tgtVal }, srcVal);
      return;
    }
    target[key] = srcVal;
  });
  return target;
};

const options = computed(() => {
  const merged = deepMerge(
    { ...defaultChartOptions },
    props.chartOptions || {}
  );

  if (!merged.onHover) merged.onHover = defaultOnHover;

  merged.plugins = merged.plugins || {};
  merged.plugins.tooltip = merged.plugins.tooltip || {};
  if (merged.plugins.tooltip.enabled === undefined) {
    merged.plugins.tooltip.enabled = false;
  }
  if (!merged.plugins.tooltip.external) {
    merged.plugins.tooltip.external = defaultExternalTooltip;
  }

  return merged;
});

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <Bar :data="collection" :options="options" />
</template>
